import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, MessageSquare, Users, Store, LogOut, UserCog, TicketPercent, ScanLine, CreditCard, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import dayjs from "dayjs";

const baseItems = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/feedback", label: "Feedback", icon: MessageSquare, testid: "nav-feedback" },
  { to: "/admin/customers", label: "Pelanggan", icon: Users, testid: "nav-customers" },
  { to: "/admin/outlets", label: "Outlet", icon: Store, testid: "nav-outlets" },
  { to: "/admin/coupons", label: "Kupon", icon: TicketPercent, testid: "nav-coupons" },
  { to: "/admin/redeem", label: "Redeem", icon: ScanLine, testid: "nav-redeem" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isSuper = user?.role === "super_admin";
  const items = isSuper
    ? [...baseItems, { to: "/admin/users", label: "Pengguna", icon: UserCog, testid: "nav-users" },
       { to: "/admin/subscriptions", label: "Langganan", icon: CreditCard, testid: "nav-subscriptions" },
       { to: "/admin/account", label: "Pengaturan Akun", icon: Settings, testid: "nav-account" }]
    : baseItems;

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col md:flex-row">
      <aside className="md:w-64 md:min-h-screen bg-white border-b md:border-b-0 md:border-r border-zinc-200 p-4 md:p-5 shrink-0">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/assets/jagaomzet-logo.svg" alt="JagaOmzet" className="w-9 h-9 rounded-xl" />
          <div className="leading-tight">
            <span className="font-extrabold tracking-tight block">JagaOmzet</span>
            <span className="text-[10px] text-zinc-400 font-medium">Pembeli Balik, Omzet Naik.</span>
          </div>
        </div>
        <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap ${isActive ? "bg-emerald-50 text-emerald-700" : "text-zinc-600 hover:bg-zinc-100"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-8 pt-5 border-t border-zinc-200">
          <div className="text-xs text-zinc-500 mb-2">Masuk sebagai</div>
          <div className="text-sm font-medium text-zinc-900 truncate" data-testid="admin-user-email">{user?.email}</div>
          <div className="mt-1">
            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isSuper ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`} data-testid="admin-role-badge">
              {isSuper ? "Super Admin" : "Admin Outlet"}
            </span>
          </div>
          {!isSuper && user?.subscription && (
            <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3" data-testid="subscription-info">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Paket</div>
              <div className="text-sm font-bold text-zinc-900 mt-0.5" data-testid="subscription-plan">{user.subscription.plan}</div>
              <div className="text-xs text-zinc-500 mt-1">
                {user.subscription.status === "TRIAL"
                  ? `Trial • berlaku sampai ${user.subscription.trial_ends_at ? dayjs(user.subscription.trial_ends_at).format("DD MMM YYYY, HH:mm") : "—"}`
                  : user.subscription.expires_at
                    ? `Berlaku sampai ${dayjs(user.subscription.expires_at).format("DD MMM YYYY, HH:mm")}`
                    : user.subscription.status}
              </div>
              {user.subscription.remaining_days != null && (
                <div className="text-xs font-semibold text-zinc-700 mt-1" data-testid="subscription-remaining">
                  Sisa {user.subscription.remaining_days} hari
                </div>
              )}
            </div>
          )}
          <Button variant="outline" size="sm" className="mt-3 w-full" onClick={onLogout} data-testid="logout-button">
            <LogOut size={14} className="mr-2" /> Keluar
          </Button>
        </div>
      </aside>
      <main className="flex-1 p-4 md:p-8 max-w-7xl w-full mx-auto">
        <Outlet />
      </main>
    </div>
  );
}
